import { cn } from '@foundation/utils/cn';
import { ReactNode } from 'react';

import {
  getResponsiveEntries,
  HeadlessModal,
  HeadlessModalRootProps,
  HeadlessModalStyles,
  resolveResponsiveProp,
  type Breakpoint,
  type Responsive,
} from '@design-system/headless-ui';

import { CloseButton } from './CloseButton';

type ModalSize = 'small' | 'medium' | 'large';

export type ModalProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly title?: ReactNode;
  readonly description?: ReactNode;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  /** Desktop-only max-width (sm: 400px, md: 560px, lg: 768px); ignored on mobile, where the modal renders as a full-width drawer. */
  readonly size?: Responsive<ModalSize>;
  readonly align?: 'center' | 'left';
  readonly closePrevented?: boolean;
  readonly showCloseButton?: boolean;
  readonly disableDefaultAnimation?: boolean;
  readonly swipeDownToClose?: boolean;
  readonly initialFocus?: HeadlessModalRootProps['initialFocus'];
  readonly finalFocus?: HeadlessModalRootProps['finalFocus'];
  readonly 'data-testid'?: string;
};

// Widths from the Figma Modal spec (node 439:364). Mobile always renders as a full-width
// drawer regardless of size, so the 'sm' tier is a deliberate no-op.
const SIZE_CLASSNAMES = {
  base: {
    small: 'md:max-w-[400px]',
    medium: 'md:max-w-[560px]',
    large: 'md:max-w-[768px]',
  },
  sm: { small: '', medium: '', large: '' },
  md: {
    small: 'md:max-w-[400px]',
    medium: 'md:max-w-[560px]',
    large: 'md:max-w-[768px]',
  },
  lg: {
    small: 'lg:max-w-[400px]',
    medium: 'lg:max-w-[560px]',
    large: 'lg:max-w-[768px]',
  },
  xl: {
    small: 'xl:max-w-[400px]',
    medium: 'xl:max-w-[560px]',
    large: 'xl:max-w-[768px]',
  },
} as const satisfies Record<Breakpoint, Record<ModalSize, string>>;

const BASE_PANEL_STYLES = cn(
  'bg-pmui-bg-primary text-pmui-fg-primary',
  'max-h-[90vh] rounded-t-md fl:rounded-t-2xl',
  'md:max-h-[85vh] md:rounded-md fl:md:rounded-2xl',
);

const SHARED_MODAL_STYLES: Omit<HeadlessModalStyles, 'panel'> = {
  overlay: 'bg-pmui-bg-inverse/(--opacity-48) backdrop-blur-(--blur-400)',
  swipeHandle:
    'mt-2 h-1 w-12 rounded-full bg-pmui-interactive-bg-default-pressed',
  closeButtonContainer: 'p-4',
  header: 'text-pmui-fg-primary px-6 md:py-6 min-h-16 pr-16',
  body: 'flex flex-col gap-4 py-6 px-6 md:py-3',
  buttonGroup: 'sticky bottom-0 px-5 py-4 bg-pmui-bg-primary',
  title: 'text-xl leading-8 tracking-tight font-semibold',
};

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size,
  align = 'center',
  closePrevented = false,
  showCloseButton = true,
  disableDefaultAnimation = false,
  swipeDownToClose = true,
  initialFocus,
  finalFocus,
  'data-testid': dataTestId,
}: ModalProps) {
  const styles: HeadlessModalStyles = {
    ...SHARED_MODAL_STYLES,
    panel: cn(
      BASE_PANEL_STYLES,
      getResponsiveEntries(resolveResponsiveProp(size, 'medium')).map(
        ([bp, s]) => SIZE_CLASSNAMES[bp][s],
      ),
    ),
  };

  return (
    <HeadlessModal.Root
      isOpen={isOpen}
      onClose={onClose}
      closePrevented={closePrevented}
      showCloseButton={showCloseButton}
      disableDefaultAnimation={disableDefaultAnimation}
      swipeDownToClose={swipeDownToClose}
      initialFocus={initialFocus}
      finalFocus={finalFocus}
      closeButton={
        <CloseButton
          size="medium"
          variant="circle"
        />
      }
      styles={styles}
      data-testid={dataTestId}
    >
      {title && (
        <HeadlessModal.Header>
          <div className="min-w-0">
            {title &&
              (typeof title === 'string' ? (
                <HeadlessModal.Title>{title}</HeadlessModal.Title>
              ) : (
                title
              ))}
          </div>
        </HeadlessModal.Header>
      )}

      {description &&
        (typeof description === 'string' ? (
          <HeadlessModal.Description>{description}</HeadlessModal.Description>
        ) : (
          description
        ))}

      <HeadlessModal.Body
        className={cn({
          'text-center': align === 'center',
          'text-left': align === 'left',
        })}
      >
        {children}
      </HeadlessModal.Body>

      {footer && (
        <HeadlessModal.ButtonGroup>{footer}</HeadlessModal.ButtonGroup>
      )}
    </HeadlessModal.Root>
  );
}

export const ModalClose = HeadlessModal.Close;
